// =============================================================================
// features/assistant/blocks/MarkAsLostModal.tsx — Confirmação humana de
// "marcar como perdido" a partir do bloco `stage_leads` do copiloto (F30-S04).
//
// Não existe estágio "Perdido" no Kanban (docs/22-agente-interno-acoes.md +
// slot F30-S04) — perder um lead é mudar `leads.status` para `closed_lost`
// via PATCH /api/leads/:id, não mover card. `LeadUpdateSchema`
// (@elemento/shared-schemas) não tem campo dedicado de "motivo" — decisão
// registrada no PR: o motivo entra em `notes`, sempre CONCATENADO ao que já
// existe (nunca sobrescreve nota do agente/IA). Por isso este modal busca o
// lead atual (useLead) antes de aplicar o PATCH (useUpdateLead — mesmo hook
// usado pelo CRM, já invalida detalhe + lista de leads).
//
// Motivo é OBRIGATÓRIO (diferente de EscalateToCreditModal, onde a nota é
// opcional) — ação destrutiva, evita clique acidental e alimenta o relatório
// de perda (doc de negócio F30).
//
// Mesma chrome de EscalateToCreditModal.tsx: createPortal, overlay, elev-5,
// foco no mount, Escape bloqueado durante a mutation.
//
// LGPD: `reason` é texto livre do operador — nunca persistido em
// localStorage/sessionStorage, só vive em memória enquanto o modal está
// aberto (vira parte de `notes` do lead ao confirmar, mesmo tratamento do
// campo hoje).
// =============================================================================

import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../../../components/ui/Button';
import { useLead } from '../../../hooks/crm/useLead';
import { useUpdateLead } from '../../../hooks/crm/useUpdateLead';
import { cn } from '../../../lib/cn';

interface MarkAsLostModalProps {
  leadId: string;
  leadName: string;
  onClose: () => void;
}

export const LOST_REASON_MAX_LENGTH = 500;
/** Espelha LeadCreateBaseSchema.notes.max(4096) (@elemento/shared-schemas). */
const NOTES_MAX_LENGTH = 4096;

/** true quando o motivo é branco/vazio — motivo é obrigatório nesta ação
 * (diferente da nota opcional de EscalateToCreditModal). Função pura,
 * testável sem montar React. */
export function isLostReasonInvalid(reason: string): boolean {
  return reason.trim().length === 0;
}

/** Concatena o motivo às notas existentes do lead — nunca sobrescreve o que
 * já existe (nota do agente/IA). Trunca ao teto do schema se necessário.
 * Função pura, testável sem montar React.
 *
 * Prioridade de truncamento (achado de segurança MODERADO, F30-S04): o
 * motivo novo é texto que o operador acabou de digitar para justificar uma
 * ação DESTRUTIVA e é sempre obrigatório — ele nunca pode ser cortado em
 * silêncio. Se `existingNotes` + motivo estourar o teto de 4096 chars do
 * schema, quem cede espaço são as notas ANTIGAS (mantemos o trecho mais
 * recente delas, cortando pelo início). Só truncamos o próprio motivo como
 * último recurso, se ele sozinho (com o prefixo) já estourar o teto — hoje
 * isso é praticamente impossível dado o `LOST_REASON_MAX_LENGTH` (500) do
 * textarea, mas a função continua correta mesmo se esse limite mudar. */
export function buildLostNotes(existingNotes: string | null, reason: string): string {
  const trimmedReason = reason.trim();
  const reasonSegment = `[Motivo de perda] ${trimmedReason}`;

  if (reasonSegment.length > NOTES_MAX_LENGTH) {
    return reasonSegment.slice(0, NOTES_MAX_LENGTH);
  }

  if (!existingNotes) {
    return reasonSegment;
  }

  const separator = '\n\n';
  const availableForExisting = NOTES_MAX_LENGTH - reasonSegment.length - separator.length;
  if (availableForExisting <= 0) {
    return reasonSegment;
  }

  const truncatedExisting =
    existingNotes.length > availableForExisting
      ? existingNotes.slice(existingNotes.length - availableForExisting)
      : existingNotes;

  return `${truncatedExisting}${separator}${reasonSegment}`;
}

export function MarkAsLostModal({
  leadId,
  leadName,
  onClose,
}: MarkAsLostModalProps): React.JSX.Element {
  const { lead, isLoading: isLoadingLead } = useLead(leadId);
  const [reason, setReason] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const [inlineError, setInlineError] = React.useState<string | null>(null);

  const { updateLead, isPending } = useUpdateLead(leadId, {
    onSuccess: onClose,
    onError: setInlineError,
  });

  const dialogRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  React.useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !isPending) onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isPending, onClose]);

  const reasonInvalid = isLostReasonInvalid(reason);

  function handleConfirm(): void {
    setTouched(true);
    setInlineError(null);
    if (reasonInvalid) return;

    updateLead({
      status: 'closed_lost',
      notes: buildLostNotes(lead?.notes ?? null, reason),
    });
  }

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40"
        style={{ background: 'rgba(10, 18, 40, 0.50)' }}
        aria-hidden="true"
        onClick={() => {
          if (!isPending) onClose();
        }}
      />

      {/* Modal panel */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mark-lost-modal-title"
        aria-describedby="mark-lost-modal-desc"
        tabIndex={-1}
        className={cn(
          'fixed z-50 inset-4 md:inset-auto',
          'md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2',
          'md:w-[min(480px,90vw)]',
          'flex flex-col rounded-md border border-border overflow-hidden',
          'focus:outline-none',
        )}
        style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-5)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b border-border-subtle shrink-0"
          style={{ background: 'var(--bg-elev-2)' }}
        >
          <h2
            id="mark-lost-modal-title"
            className="font-display font-bold text-ink truncate"
            style={{ fontSize: 'var(--text-lg)', letterSpacing: '-0.03em' }}
          >
            Marcar como perdido
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            aria-label="Fechar modal"
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded-sm shrink-0',
              'text-ink-3 hover:text-ink hover:bg-surface-hover',
              'transition-all duration-fast ease-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
              'disabled:opacity-40 disabled:pointer-events-none',
            )}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex flex-col gap-4">
          <p
            id="mark-lost-modal-desc"
            className="font-sans text-sm text-ink"
            style={{ lineHeight: 1.5 }}
          >
            <strong className="font-semibold">{leadName}</strong> será marcado como perdido no CRM.
            O motivo abaixo é anexado às notas do lead.
          </p>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="lost-reason"
              className="font-sans font-semibold text-ink-2 uppercase"
              style={{ fontSize: 'var(--text-xs)', letterSpacing: '0.1em' }}
            >
              Motivo <span className="font-normal text-danger normal-case">(obrigatório)</span>
            </label>
            <textarea
              id="lost-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              disabled={isPending || isLoadingLead}
              placeholder="Ex.: sem retorno após 3 tentativas, desistiu do crédito, perfil não elegível..."
              maxLength={LOST_REASON_MAX_LENGTH}
              aria-invalid={touched && reasonInvalid}
              aria-describedby="lost-reason-error"
              className={cn(
                'w-full font-sans font-medium text-ink',
                'bg-surface-1 rounded-sm px-[14px] py-[11px]',
                'border',
                touched && reasonInvalid ? 'border-danger' : 'border-border-strong',
                'shadow-[inset_0_1px_2px_var(--border-inner-dark)]',
                'transition-[border-color,box-shadow] duration-fast ease',
                'placeholder:text-ink-4',
                'hover:border-ink-3',
                'focus:outline-none focus:border-azul',
                'focus:shadow-[0_0_0_3px_rgba(27,58,140,0.15),inset_0_1px_2px_var(--border-inner-dark)]',
                'disabled:opacity-60 disabled:cursor-not-allowed',
                'resize-none',
              )}
              style={{ fontSize: 'var(--text-sm)' }}
            />
            <div className="flex items-center justify-between">
              {touched && reasonInvalid ? (
                <span id="lost-reason-error" className="font-sans text-xs text-danger">
                  Informe o motivo antes de confirmar.
                </span>
              ) : (
                <span />
              )}
              <span
                className="font-mono text-ink-4 shrink-0"
                style={{ fontSize: 'var(--text-xs)' }}
              >
                {reason.length}/{LOST_REASON_MAX_LENGTH}
              </span>
            </div>
          </div>

          {inlineError && (
            <div
              className="flex items-start gap-2 px-3 py-2.5 rounded-xs"
              role="alert"
              style={{
                background: 'var(--danger-bg)',
                border: '1px solid var(--danger)',
                borderLeftWidth: 3,
              }}
            >
              <p className="font-sans text-sm text-ink">{inlineError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border-subtle shrink-0">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleConfirm}
            disabled={isPending || isLoadingLead}
          >
            {isPending ? 'Aplicando...' : 'Confirmar perda'}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}
